import { EmissionRef } from '../ghg-emission/emission.model';
import { InputSummary, InputSummaryItem } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { LcfsRin } from '../lcfs-rin.model';
import { UnitValue } from '../../data-structures/unit-value.model';
import {PriceStructure, PriceStructureRangeOf} from '../financial/products/price-structure.model';
import {v4 as uuid} from 'uuid';
import AnessaListItemUtil, { AnessaListItem } from '../../anessa-list-item.interface';


export interface ProductInterface {
  _id: string;
  name: string;
  uuid: string;
  type: ProductType;
  description: string;
  internalDemand: UnitValue;
  priceType: string;
  price: UnitValue;
  quantity: UnitValue;
  cpi: UnitValue;
  emission: EmissionRef;
  lcfsRin: LcfsRin;
  priceIsLcfsRin: boolean;
  priceIsVariable: boolean;
  priceStructures: PriceStructure[];
  rangeOf: PriceStructureRangeOf;
  ispricechanged : boolean;
}

export class Product implements AnessaListItem {
  public _id: string;
  public name: string;
  uuid: string;
  public type: ProductType;
  public description: string;
  public internalDemand: UnitValue;
  public priceType: string;
  public price: UnitValue;
  public quantity: UnitValue;
  public cpi: UnitValue;
  public emission: EmissionRef;
  lcfsRin: LcfsRin;
  public priceIsLcfsRin: boolean;
  public priceIsVariable: boolean;
  public priceStructures: PriceStructure[];
  public rangeOf: PriceStructureRangeOf;
  public ispricechanged : boolean;
  constructor(param: ProductInterface = {
    _id: null,
    name: '',
    uuid: null,
    type: null,
    description: '',
    internalDemand: new UnitValue(),
    priceType: null,
    price: new UnitValue(),
    quantity: new UnitValue(),
    cpi: new UnitValue(),
    emission: new EmissionRef(),
    lcfsRin: new LcfsRin(),
    priceIsLcfsRin: false,
    priceIsVariable: false,
    priceStructures: [],
    rangeOf: PriceStructureRangeOf.gross,
    ispricechanged : false,
  }) {
    if (param._id) {
      this._id = param._id;
    } else {
      delete this._id;
    }
    this.name = param.name;
    this.uuid = param.uuid ? param.uuid : uuid();
    this.type = param.type;
    this.description = param.description;
    this.internalDemand = new UnitValue(param.internalDemand);
    if (!this.isSellableDigestate()) {
      this.priceType = ProductFeeType.INCOME;
    } else {
      this.priceType = param.priceType;
    }
    this.price = new UnitValue(param.price);
    this.quantity = new UnitValue(param.quantity);
    this.cpi = new UnitValue(param.cpi);
    this.emission = new EmissionRef(param.emission);
    this.lcfsRin = new LcfsRin(param.lcfsRin);
    this.priceIsVariable = param.priceIsVariable;
    // Added for candeactivate if the value of the price changes owing to change in the value of LCFS / RIN 
    this.ispricechanged = param.ispricechanged;
    // gui will show variable over lcfsrin price so disable lcfsrin price if this is set
    // they should not be set at the same time, but it happened and this is the fix
    if (this.priceIsVariable) {
      this.priceIsLcfsRin = false;
    } else {
      this.priceIsLcfsRin = param.priceIsLcfsRin;
    }
    this.priceStructures = [];
    if (param.priceStructures) {
      param.priceStructures.forEach(ps => this.priceStructures.push(new PriceStructure(ps)));
    }
    this.rangeOf = param.rangeOf;
  }

  static deepCopy(product: Product): Product {
    let copy;
    if (product) {
      copy = new Product(product);
      return copy;
    }
    throw new Error('Product copy failed');
  }

  /**
   * Returns a copy of the provided array of products while keeping their types.
   * @param generatedProducts is an array of Product objects.
   */
  static arrayDeepCopy(generatedProducts: Product[]) {
    const copy: Product[] = [];
    if (generatedProducts && generatedProducts.length) {
      for (const feedstock of generatedProducts) {
        copy.push(new Product(feedstock));
      }
    }
    return copy;
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);
    return inputSummary;
  }

  getFinancialInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    // inputsSummary.inputs = this.getFinancialSummaryInputs();
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, ProductPropName.priceIs, this.priceIsVariable ? 'PROJECT.FINANCIAL_GEN_PROD.VARIABLE' : 'PROJECT.FINANCIAL_GEN_PROD.FIXED', [...path, 'priceIsVariable']);
    if (this.priceIsVariable) {
      inputs = InputSummaryUtils.performPush(inputs, ProductPropName.amountRangeOf, this.rangeOf, [...path, 'rangeOf']);
      for (const priceStruct of this.priceStructures) {
        const index = this.priceStructures.indexOf(priceStruct);
        let name = priceStruct.name;
        if (!name) { name = '*no name*'; }
        const psSummary = new InputSummary();
        psSummary.inputs = priceStruct.getSummaryInputs([...path, 'priceStructures', String(index)]);
        inputSummary.subSection[name] = psSummary;
      }
    } else {
      let priceType = '';
      switch (this.priceType) {
        case 'cost':
          priceType = 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.COST';
          break;
        case 'income':
          priceType = 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.INCOME';
          break;
        case 'free':
          priceType = 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.FREE';
          break;
        case 'tipping fee':
          priceType = 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.TIPPING_FEE';
          break;
      }
      inputs = InputSummaryUtils.performPush(inputs, ProductPropName.priceType, priceType, [...path, 'priceType']);
      inputs = InputSummaryUtils.performPush(inputs, ProductPropName.price, this.price, [...path, 'price']);
      inputs = InputSummaryUtils.performPush(inputs, ProductPropName.quantity, this.quantity, [...path, 'quantity']);
      inputs = InputSummaryUtils.performPush(inputs, ProductPropName.cpi, this.cpi, [...path, 'cpi']);
      if (this.hasInternalDemand()) {
        inputs = InputSummaryUtils.performPush(inputs, ProductPropName.internalDemand, this.internalDemand, [...path, 'internalDemand']);
      }
    }
    inputSummary.inputs = inputs;
    return inputSummary;
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    // inputs = InputSummaryUtils.performPush(inputs, ProductPropName.description, this.description);
    inputs = InputSummaryUtils.performPush(inputs, ProductPropName.internalDemand, this.internalDemand, [...path, 'internalDemand']);
    return inputs;
  }

  /**
   * Returns a boolean for if the current product has internal demand based on its type
   */
  hasInternalDemand() {
    return this.type === ProductType.Heat ||
      this.type === ProductType.Electric ||
      this.type === ProductType.WholeDigestate ||
      this.type === ProductType.ThinDigestate ||
      this.type === ProductType.SolidDigestate;
  }

  /**
   * Cleans any properties that are not needed depending on the current configuration.
   * Also sets the price type to income if the product doesn't need the price type set by the user.
   */
  clean() {
    if (this.priceType === ProductFeeType.FREE) {
      this.price = new UnitValue();
      this.quantity = new UnitValue();
      this.cpi = new UnitValue();
    }
    // if not selectable always income
    if (!this.isSellableDigestate()) {
      this.priceType = ProductFeeType.INCOME;
    }
    // Added for candeactivate if the value of the price changes owing to change in the value of LCFS / RIN 
    this.ispricechanged = false;
  }

  /**
   * Returns a boolean for is the product should have a selectable price type.
   */
  isSellableDigestate() {
    return this.type === ProductType.WholeDigestate || this.type === ProductType.ThinDigestate || this.type === ProductType.SolidDigestate;
  }

  getUnits(): string[] {
    switch (this.type) {
      case ProductType.Electric:
        return ['Energy'];
      case ProductType.Heat:
        return ['Thermal_Quantity'];
      case ProductType.Biometh:
        return ['Volume_Gas'];
      case ProductType.SolidDigestate:
      case ProductType.WholeDigestate:
      case ProductType.ThinDigestate:
        return ['Mass'];
      default:
        return [];
    }
  }

  getUuidForPath(): string {
    return AnessaListItemUtil.getUuidForPath(this.uuid);
  }

  canCalculateLcfsrin() {
    return this.lcfsRin?.rinEnabled   || this.lcfsRin?.lcfsEnabled ||
      (this.lcfsRin.natgasEnabled && this.type !== ProductType.Electric);
  }

  disableLcfsrin() {
    if (!this.lcfsRin) return;
    this.lcfsRin.rinEnabled = false;
    this.lcfsRin.lcfsEnabled = false;
    this.lcfsRin.natgasEnabled = false;
  }
}

export enum ProductPropName {
  name = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.NAME',
  description = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.DESCRIPTION',
  type = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.TYPE',
  internalDemand = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.INTERNAL_DEMAND',
  priceType = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_TYPE',
  price = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE',
  quantity = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.QUANTITY',
  cpi = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.CPI',
  emission = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.EMISSION',
  lcfsRin = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.LCFSRIN',
  priceIs = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE_TYPE',
  amountRangeOf = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.RANGE_TYPE',
  id_name = 'name',
  id_description = 'description',
  id_type = 'type',
  id_internalDemand = 'internal demand',
  id_priceType = 'price type',
  id_price = 'price',
  id_quantity = 'quantity',
  id_cpi = 'CPI',
  id_emission = 'emission',
  id_lcfsRin = 'LCFS RIN',
  id_priceIs = 'price is',
  id_amountRangeOf = 'price structure range type',
}

export enum ProductType {
  Electric = 'electric',
  Heat = 'heat',
  Biometh = 'biometh',
  WholeDigestate = 'whole digestate biofertilizer',
  SolidDigestate = 'solid digestate biofertilizer',
  ThinDigestate = 'thin digestate biofertilizer'
}

/**
 * Enum for available options for financial price types.
 *
 * -- IMPORTANT --
 *
 * These are values used for ado in java for the financial price type num, they can't be
 * changed here without breaking either the java enum or ado.
 * Use this strictly as a value that will be saved, the display value for this should be handled by
 * using this as a translation id.
 */
export enum ProductFeeType {
  INCOME = 'income',
  COST = 'cost',
  FREE = 'free',
  TIPPING_FEE = 'tipping fee'
}
